import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'src/store/rootReducer';
import {
  Box,
  makeStyles,
  createStyles,
  Typography,
  Checkbox as MaterialCheckbox,
  withStyles,
  Input as MaterialInput,
} from '@material-ui/core';
import { colors } from 'design-system';
import {
  requestPinValidation,
  requestPinCreation,
  cleanValidationErrors,
} from 'src/store/signature/actions';
import { useNetworkObserver } from 'data-layer-cr/dist/network';
import { setStepperEmission } from 'src/store/steppers/actions';
import {
  REQUEST_VALIDATE_PIN,
  REQUEST_CREATE_PIN,
} from 'src/store/signature/requestIdentifiers';
import PinCardLayout from './PinCardLayout';

const CustomCheckbox = withStyles({
  root: {
    padding: 0,
    paddingRight: 9,
  },
  checked: {
    color: `${colors.bright_turquoise} !important`,
  },
  colorPrimary: {
    color: colors.bright_turquoise,
  },
})(MaterialCheckbox);

const CustomInput = withStyles({
  root: { paddingLeft: 0 },
  input: {
    paddingLeft: 0,
    '&::placeholder': {
      opacity: 1,
    },
  },
  error: {
    '&::before': {
      borderColor: '#bb0016',
    },
  },
})(MaterialInput);

const useStyles = makeStyles(() =>
  createStyles({
    text: {
      fontWeight: 400,
      fontSize: 15,
      lineHeight: '22px',
      color: '#4a4b4e',
    },
    checkText: {
      fontWeight: 400,
      fontSize: 15,
      lineHeight: '22px',
    },
    helperText: {
      fontWeight: 500,
      fontSize: 13,
      lineHeight: '20px',
      color: colors.light_silver,
    },
    errorText: {
      color: '#bb0016',
      fontWeight: 500,
      fontSize: 13,
      lineHeight: '20px',
    },
    resendText: {
      fontWeight: 600,
      fontSize: 13,
      lineHeight: '20px',
      color: colors.bright_turquoise,
      textDecoration: 'underline',
      cursor: 'pointer',
    },
  }),
);

export const ValidatePinCard = ({
  isMobile,
  validationPinCode,
  validationPinError,
  currentStepEmission,
  signatureCompleted,
  orderId,
  requestPinCreationAction,
  requestPinValidationActions,
  setStepperEmissionAction,
  cleanValidationErrorsAction,
}: Props): JSX.Element => {
  const classes = useStyles();
  const navigate = useNavigate();
  const requestValidationStatus = useNetworkObserver(REQUEST_VALIDATE_PIN);
  const requestPinStatus = useNetworkObserver(REQUEST_CREATE_PIN);
  const requestingValidation = ['requesting'].includes(requestValidationStatus);
  const requestingPin = ['requesting'].includes(requestPinStatus);
  const [pin, setPin] = useState('');
  const [checkbox, setCheckbox] = useState(false);

  const buttonDisabled = !checkbox || pin.length !== 6;

  const handleCancelSignature = () => {
    const newStep = currentStepEmission - 1;
    setStepperEmissionAction(newStep);
  };

  const handleValidatePin = () => {
    requestPinValidationActions(pin);
  };

  const handleRequestPin = () => {
    if (!requestingPin) {
      requestPinCreationAction();
    }
  };

  const handleChangeInput = (value: string) => {
    setPin(value);
    if (validationPinError) {
      cleanValidationErrorsAction();
    }
  };

  useEffect(() => {
    if (signatureCompleted) {
      navigate(`/emission/continue?accepted=True&uniqueId=${orderId}`);
    }
  }, [signatureCompleted]);

  return (
    <PinCardLayout
      buttonText="Certificar documento"
      onCancel={handleCancelSignature}
      onMainClick={handleValidatePin}
      disabled={buttonDisabled || signatureCompleted}
      isMobile={isMobile}
      loading={requestingValidation}
      data-testid="pin-card-layout"
    >
      <Box sx={{ mt: 2 }}>
        <Typography className={classes.text}>
          Coloca el PIN de seguridad que hemos enviado a tu correo electrónico.
        </Typography>
      </Box>
      <Box sx={{ my: 2 }} data-testid="input-section">
        <Box sx={{ pl: 1, pr: 3 }}>
          <CustomInput
            placeholder="Introducir PIN seguridad"
            fullWidth
            id="swi-pin-value-input"
            data-testid="swi-pin-value-input"
            name="pinValue"
            value={pin}
            onChange={(e) => handleChangeInput(e.target.value)}
            style={{ color: validationPinError ? '#bb0016' : '#4a4b4e' }}
            error={validationPinError}
          />
        </Box>
        {!validationPinError && (
          <Box>
            <Typography className={classes.helperText}>
              ¿No has recibido un PIN?{' '}
              <Box
                display="inline"
                className={classes.resendText}
                onClick={handleRequestPin}
                data-testid="pin-request-button"
              >
                Enviar de nuevo
              </Box>
            </Typography>
          </Box>
        )}
        {validationPinError && validationPinCode === 'EXPIRED' && (
          <Box>
            <Typography className={classes.errorText}>
              El PIN ha caducado.{' '}
              <Box
                display="inline"
                className={classes.resendText}
                onClick={handleRequestPin}
                id="check-box-accept"
                data-testid="check-box-accept"
              >
                Solicitar otro PIN
              </Box>
            </Typography>
          </Box>
        )}
        {validationPinError && validationPinCode === 'INVALID' && (
          <Box>
            <Typography className={classes.errorText}>
              El PIN ingresado es incorrecto
            </Typography>
          </Box>
        )}
      </Box>
      <Box sx={{ mt: 2, mb: 3 }}>
        <Typography className={classes.text}>
          {`Haz click en "Acepto los documentos mostrados" para confirmar que he
          revisado los documentos mostrados y estoy de acuerdo con ellos.`}
        </Typography>
      </Box>
      <Box display="flex" sx={{ mb: 1 }} data-testid="checkbox-section">
        <CustomCheckbox
          value={checkbox}
          id="swi-check-box-accept"
          data-testid="swi-check-box-accept"
          name="pinConditions"
          onChange={() => setCheckbox(!checkbox)}
        />
        <Typography className={classes.checkText}>
          Acepto los documentos mostrados
        </Typography>
      </Box>
    </PinCardLayout>
  );
};

export const mapStateToProps = (
  state: RootState,
): {
  orderId: string;
  currentStepEmission: number;
  validationPinError: boolean;
  validationPinCode: string;
  signatureCompleted: boolean;
} => ({
  orderId: state.signature.orderId,
  currentStepEmission: state.stepper.currentStepEmission,
  validationPinError: state.signature.validationPinError,
  validationPinCode: state.signature.validationPinCode,
  signatureCompleted: state.signature.signatureCompleted,
});

const reduxActions = {
  requestPinCreationAction: requestPinCreation,
  requestPinValidationActions: requestPinValidation,
  setStepperEmissionAction: setStepperEmission,
  cleanValidationErrorsAction: cleanValidationErrors,
};

const connector = connect(mapStateToProps, reduxActions);
type PropsFromRedux = ConnectedProps<typeof connector>;

interface Props extends PropsFromRedux {
  isMobile?: boolean;
}
export default connector(ValidatePinCard);
